import { ar } from "date-fns/locale/ar";
import { enUS } from "date-fns/locale/en-US";
import React, { useEffect, useRef, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Ic_calendar from "../../../../src/assets/images/icons/calendar.svg";
import Ic_x from "../../../../src/assets/images/icons/x.svg";
import "./DateInput.css";
import { get } from "lodash";
registerLocale("ar", ar);
registerLocale("en", enUS);

interface DateInputProps {
  label: string;
  name: string;
  required?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  dateConstraint?: "past" | "future" | "olderThan18" | "olderThan15";
  maxDate?: Date;
  minDate?: Date;
  openDate?: boolean;
  hideErrorMessage?: boolean;
  isClearable?: boolean;
  onlyCalender?: boolean;
  className?: string;
  value?: string;
}
const DateInput: React.FC<DateInputProps> = ({
  label,
  name,
  required = false,
  dateConstraint,
  readOnly = false,
  disabled = false,
  maxDate,
  minDate,
  openDate = false,
  hideErrorMessage = false,
  isClearable = false,
  onlyCalender = false,
  className,
  value,
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.slice(0, 2);
  const locale = lang === "ar" ? "ar" : "en";

  const [isFocused, setIsFocused] = useState(false);

  const {
    watch,
    control,
    formState: { errors },
  } = useFormContext();
  const datePickerRef = useRef<any>(null);
  const inputValue = watch(name);

  const errorMessage = get(errors, name)?.message;

  const handleIconClick = () => {
    if (datePickerRef.current) {
      datePickerRef.current.setOpen(true);
    }
  };

  useEffect(() => {
    if (openDate && datePickerRef.current) {
      datePickerRef.current.setOpen(true);
    }
  }, [openDate]);

  const today = new Date();
  const eighteenYearsAgo = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate(),
  );
  const fifteenYearsAgo = new Date(
    today.getFullYear() - 15,
    today.getMonth(),
    today.getDate(),
  );

  const getDateConstraints = () => {
    switch (dateConstraint) {
      case "past":
        return { maxDate: today };

      case "future":
        return { minDate: today };

      case "olderThan18":
        return { maxDate: eighteenYearsAgo };

      case "olderThan15":
        return { maxDate: fifteenYearsAgo };

      default:
        return {};
    }
  };

  const formatDate = (date: Date | null): string => {
    if (!date) return "";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const parseDate = (dateString: string): Date => {
    const parts = dateString.split("-");
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed in Date
    const year = parseInt(parts[2], 10);
    return new Date(year, month, day);
  };

  return (
    <div className="relative flex w-full flex-col">
      <p id="birthday-description" className="sr-only">
        {t("enter_your_birthday")}
      </p>

      <Controller
        control={control}
        name={name}
        rules={{ required: required ? t("required_field") : false }}
        render={({ field }) => (
          <>
            {(field.value || value) && !onlyCalender && (
              <label
                htmlFor={name}
                className={`absolute -top-0.5 start-2 z-[3] bg-white px-1 text-sm transition-all duration-200 ${isFocused ? "text-primary" : "text-primary-text"
                  } ${readOnly ? "!bg-transparent text-body-text" : ""}`}
                style={{
                  transform:
                    field.value || value ? "translateY(-50%)" : "translateY(0)",
                }}
              >
                {label}
              </label>
            )}
            <div
              className={`h-11 w-full min-w-60 appearance-none rounded-lg border border-systemBg px-4 py-2 text-base text-primary-text placeholder-body-text focus:!border-primary focus:bg-white focus:outline-none ${readOnly || disabled ? "bg-primary-bg text-body-text" : "bg-white"} ${errorMessage ? "!border-2 !border-error bg-white" : ""} ${className ?? ""}`}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
            >
              <DatePicker
                selected={field.value ? parseDate(field.value) : null}
                onChange={(date) => field.onChange(formatDate(date))}
                dateFormat="dd-MM-yyyy"
                className={`h-full w-full bg-transparent focus:outline-none ${onlyCalender && "hidden"} ${lang === "ar" ? "rtl-datepicker" : ""
                  }`}
                portalId="root-portal"
                showYearDropdown
                maxDate={maxDate}
                minDate={minDate}
                showMonthDropdown
                dropdownMode="select"
                locale={locale}
                readOnly={readOnly}
                disabled={disabled}
                placeholderText={label}
                ref={(ref) => {
                  datePickerRef.current = ref;
                }}
                aria-label={label}
                calendarClassName={`custom-calendar ${lang === "ar" ? "rtl-calendar" : ""
                  }`}
                popperClassName="!start-20"
                value={value}
                // popperContainer={({children}) => createPortal(children,document.body)}
                dayClassName={(date) => {
                  const selected = field.value ? parseDate(field.value) : null;
                  if (!selected) return "";

                  const sameMonth =
                    date.getMonth() === selected.getMonth() &&
                    date.getFullYear() === selected.getFullYear();
                  if (sameMonth) return "";
                  return "no-active-selected";
                }}
                {...getDateConstraints()}
              />
              {field.value && !readOnly && isClearable && (
                <button
                  type="button"
                  className={`absolute end-10 ${onlyCalender && "relative flex w-48 items-center gap-1"}`}
                  onClick={() => field.onChange("")}
                >
                  <img src={Ic_x} alt="Clear icon" />
                  {onlyCalender && (
                    <span className="text-sm text-gray-500">{field.value}</span>
                  )}
                </button>
              )}
              <button
                type="button"
                className="absolute end-4"
                onClick={handleIconClick}
              >
                <img src={Ic_calendar} alt="Calendar icon" />
              </button>
            </div>
            {errorMessage && !hideErrorMessage && (
              <p className="mt-1 text-xs text-error">{`${errorMessage}`}</p>
            )}
          </>
        )}
      />
    </div>
  );
};

export default DateInput;
